import { useCallback, useState } from 'react'
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import type { RemoteSessionStatus, SellerRemoteHostRead } from '../../../api/generated/model'
import { USE_REAL_API } from '../../../lib/api'
import { useMe } from '../../../lib/useMe'
import { toRemoteAccessSafeError } from '../../admin/servers/remote-access-api'
import {
  closePreparedRemoteTab,
  postLaunchTicket,
  prepareRemoteTab,
  showRemoteTabFailure,
} from '../../admin/servers/remote-launch-handoff'
import { isSessionPollingStatus } from '../../admin/servers/server-view-model'
import { listSellerServers, readSellerSession, requestSellerLaunch } from './seller-servers-api'

export const SELLER_SERVERS_QUERY_KEY = ['seller', 'remote-access', 'hosts'] as const
const HOST_POLL_INTERVAL_MS = 15_000
const SESSION_POLL_INTERVAL_MS = 2_000

type SellerLaunchError = 'popupBlocked' | 'reauthentication' | 'inUse' | 'generic'

export type SellerLaunchState = {
  pending: boolean
  sessionId: string | null
  sessionStatus: RemoteSessionStatus | null
  error: SellerLaunchError | null
}

const IDLE: SellerLaunchState = { pending: false, sessionId: null, sessionStatus: null, error: null }

type TabMessages = { reauthentication: string; generic: string }

/** The seller's machines. A 404 means none for this seller (or the switch is off). */
export function useSellerServers() {
  const { hasCapability } = useMe()
  return useQuery({
    queryKey: SELLER_SERVERS_QUERY_KEY,
    queryFn: listSellerServers,
    enabled: USE_REAL_API && hasCapability('remote_access.view'),
    staleTime: HOST_POLL_INTERVAL_MS,
    refetchInterval: (query) => (query.state.error ? false : HOST_POLL_INTERVAL_MS),
    refetchIntervalInBackground: false,
    retry: (failureCount, error) => {
      const status = toRemoteAccessSafeError(error).status
      return status !== 401 && status !== 403 && status !== 404 && failureCount < 2
    },
  })
}

/** True when the seller has at least one machine; the menu shows Servers only then. */
export function useHasSellerServers(): boolean {
  const hosts = useSellerServers()
  return (hosts.data?.length ?? 0) > 0
}

export function useSellerServerLaunch(hosts: readonly SellerRemoteHostRead[], tab: TabMessages) {
  const queryClient = useQueryClient()
  const [byHost, setByHost] = useState<Record<string, SellerLaunchState>>({})

  const patch = useCallback((hostId: string, next: Partial<SellerLaunchState>) => {
    setByHost((current) => ({ ...current, [hostId]: { ...(current[hostId] ?? IDLE), ...next } }))
  }, [])

  const refreshHosts = useCallback(
    () => queryClient.invalidateQueries({ queryKey: SELLER_SERVERS_QUERY_KEY }),
    [queryClient],
  )

  const connect = useCallback(async (hostId: string) => {
    // The tab must open inside the click, before any await, or the browser
    // treats it as an unrequested pop-up.
    const prepared = prepareRemoteTab()
    if (!prepared) {
      patch(hostId, { ...IDLE, error: 'popupBlocked' })
      return
    }
    patch(hostId, { ...IDLE, pending: true })
    try {
      const outcome = await requestSellerLaunch(hostId, `seller-launch-${crypto.randomUUID()}`)
      if (outcome.kind === 'created') {
        postLaunchTicket(prepared, outcome.intent)
        patch(hostId, {
          pending: false,
          sessionId: outcome.intent.session_id,
          sessionStatus: outcome.intent.status,
        })
      } else {
        closePreparedRemoteTab(prepared)
        patch(hostId, { pending: false, sessionId: outcome.sessionId, sessionStatus: outcome.status })
      }
    } catch (error) {
      const safe = toRemoteAccessSafeError(error)
      const kind: SellerLaunchError =
        safe.code === 'remote_access_reauthentication_required'
          ? 'reauthentication'
          : safe.code === 'remote_access_conflict'
            ? 'inUse'
            : 'generic'
      showRemoteTabFailure(prepared, kind === 'reauthentication' ? tab.reauthentication : tab.generic)
      patch(hostId, { ...IDLE, error: kind })
    } finally {
      void refreshHosts()
    }
  }, [patch, refreshHosts, tab.generic, tab.reauthentication])

  const polled = hosts.flatMap((host) => {
    const state = byHost[host.id]
    return state?.sessionId && state.sessionStatus && isSessionPollingStatus(state.sessionStatus)
      ? [{ hostId: host.id, sessionId: state.sessionId }]
      : []
  })

  useQueries({
    queries: polled.map(({ hostId, sessionId }) => ({
      queryKey: ['seller', 'remote-access', 'session', sessionId],
      queryFn: async () => {
        const session = await readSellerSession(sessionId)
        patch(hostId, { sessionStatus: session.status })
        if (!isSessionPollingStatus(session.status)) void refreshHosts()
        return session
      },
      refetchInterval: SESSION_POLL_INTERVAL_MS,
      retry: false,
    })),
  })

  return {
    connect,
    stateFor: (hostId: string): SellerLaunchState => byHost[hostId] ?? IDLE,
  }
}
